import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ElectionResponseDto } from '@thro-clients/polyas-connector-api';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { translations } from 'locales/i18n';

import { getStatusLabel } from '../../MyElections/helper';

interface ElectionListProps {
  elections: ElectionResponseDto[];
  onDelete?: (id: string) => void;
}

export function ElectionList({ elections, onDelete }: ElectionListProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string, status: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    setSelectedStatus(status);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
    setSelectedStatus(null);
  };

  // const handleDelete = () => {
  //   if (selectedId && onDelete) {
  //     onDelete(selectedId);
  //   }
  //   handleMenuClose();
  // };

  const isEditable = selectedStatus === 'future';

  const statusColorMap: Record<string, 'success' | 'warning' | 'default'> = {
    active: 'success',
    upcoming: 'warning',
    ended: 'default'
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {elections.map(election => (
        <Card
          key={election.id}
          sx={{
            transition: 'box-shadow 0.2s',
            '&:hover': { boxShadow: 3 }
          }}
        >
          <CardContent>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Link
                    to={`/election/admin/${election.id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {election.title}
                    </Typography>
                  </Link>
                  <Chip
                    label={getStatusLabel(election.status, t)}
                    color={statusColorMap[election.status]}
                    size="small"
                  />
                </Box>
                {election.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {election.description}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={e => handleMenuOpen(e, election.id, election.status)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => navigate(`/election/admin/${election.id}`)}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <CalendarTodayIcon fontSize="small" />
                <Typography variant="body2">
                  {format(new Date(election.electionStartDate), 'dd.MM.yyyy', { locale: de })}
                  {' - '}
                  {format(new Date(election.electionEndDate), 'dd.MM.yyyy', { locale: de })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <GroupIcon fontSize="small" />
                <Typography variant="body2">
                  {election.alreadyVotedEligibleVoterCount} / {election.eligibleVoterCount}{' '}
                  {t(translations.pages.polyas.list.voted())}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <Tooltip
          title={!isEditable ? t(translations.pages.polyas.edit.notEditableTooltip()) : ''}
          placement="left"
          arrow
        >
          <span>
            <MenuItem
              disabled={!isEditable}
              onClick={() => {
                handleMenuClose();
                navigate(`/election/admin/edit/${selectedId}`);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t(translations.pages.polyas.list.edit())}</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
        {/* <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Löschen</ListItemText>
        </MenuItem> */}
      </Menu>
    </Box>
  );
}
